import React, { useState } from 'react';
import { theme, alpha } from '../theme';
import { DatePickerModal } from './DatePickerModal';

// Helper function para crear fechas sin problemas de zona horaria
const createLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0, 0);
};

// Helper function para formatear fecha a string
const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface DateInputProps {
  value: string;
  onChange: (date: string) => void;
  label: string;
  required?: boolean;
}

interface TimeInputProps {
  value: string;
  onChange: (time: string) => void;
  label: string;
  required?: boolean;
}

// Componente personalizado para fecha
export const DateInput: React.FC<DateInputProps> = ({ value, onChange, label, required = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = formatDateToString(date);
      onChange(formattedDate);
      setIsOpen(false);
    }
  };

  const selectedDate = value ? createLocalDate(value) : null;
  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
      <label style={{
        color: theme.colors.text,
        fontFamily: theme.fonts.body,
        fontWeight: 600,
        fontSize: theme.fonts.sizeBody
      }}>
        {label} {required && <span style={{ color: theme.colors.danger }}>*</span>}
      </label>
      
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '100%',
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            border: `2px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.input,
            fontSize: theme.fonts.sizeBody,
            fontFamily: theme.fonts.body,
            backgroundColor: theme.colors.background,
            color: value ? theme.colors.text : theme.colors.textSecondary,
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <span>
            {value ? createLocalDate(value).toLocaleDateString('es-ES', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : 'Seleccionar fecha'}
          </span>
          <span style={{ fontSize: '16px' }}>📅</span>
        </button>
        
        <DatePickerModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          selectedDate={selectedDate}
          onChange={handleDateChange}
          minDate={minDate}
        />
      </div>
    </div>
  );
};

// Componente personalizado para hora
export const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, label, required = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const timeSlots = [];
  for (let hour = 6; hour <= 23; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      timeSlots.push(timeString);
    }
  }

  const handleTimeSelect = (time: string) => {
    onChange(time);
    setIsOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
      <label style={{
        color: theme.colors.text,
        fontFamily: theme.fonts.body,
        fontWeight: 600,
        fontSize: theme.fonts.sizeBody
      }}>
        {label} {required && <span style={{ color: theme.colors.danger }}>*</span>}
      </label>
      
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '100%',
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            border: `2px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.input,
            fontSize: theme.fonts.sizeBody,
            fontFamily: theme.fonts.body,
            backgroundColor: theme.colors.background,
            color: value ? theme.colors.text : theme.colors.textSecondary,
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <span>
            {value ? value : 'Seleccionar horario'}
          </span>
          <span style={{ fontSize: '16px' }}>🕐</span>
        </button>
        
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            marginTop: '4px',
            backgroundColor: theme.colors.surface,
            border: `2px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.card,
            boxShadow: theme.colors.shadow,
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {timeSlots.map((time) => (
              <button
                key={time}
                type="button"
                onClick={() => handleTimeSelect(time)}
                style={{
                  width: '100%',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  border: 'none',
                  backgroundColor: value === time ? theme.colors.primary : 'transparent',
                  color: value === time ? theme.colors.white : theme.colors.text,
                  fontSize: theme.fonts.sizeBody,
                  fontFamily: theme.fonts.body,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  borderBottom: `1px solid ${theme.colors.border}`
                }}
                onMouseEnter={(e) => {
                  if (value !== time) {
                    e.currentTarget.style.backgroundColor = `${alpha('primary', '20')}`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (value !== time) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {time}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
